import {Box, Button, Card, CardContent, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {HugeiconsIcon} from "@hugeicons/react";
import {Delete02Icon, PencilEdit01Icon} from "@hugeicons/core-free-icons";
import moment from "moment";
import {collectionCardSx, collectionCardContentSx, collectionCardTitleSx, collectionCardDescriptionSx} from "../../../../components/collectionCardStyles";
import {extensionLabel} from "../hooks/useAmiExtensions";

const formatDate = value => value && moment(value).isValid() ? moment(value).format("DD MMM YYYY") : "Not set";

export const CampaignCard = ({campaign, extension, onOpen, onEdit, onDelete}) => (
    <Card onClick={() => onOpen(campaign)} sx={{...collectionCardSx, cursor: "pointer", display: "flex", minWidth: 0, "&:hover": {transform: "scale(1.01)"}}}>
        <CardContent sx={collectionCardContentSx}>
            <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1}>
                <Box sx={{minWidth: 0}}>
                    <Button
                        onClick={event => {event.stopPropagation(); onOpen(campaign);}}
                        sx={collectionCardTitleSx}
                    >
                        <Typography fontSize={16} fontWeight={600}>{campaign.name}</Typography>
                    </Button>
                    {campaign.description && (
                        <Typography sx={collectionCardDescriptionSx}>
                            {campaign.description}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" sx={{flexShrink: 0}}>
                    <Tooltip title="Edit campaign">
                        <IconButton size="small" aria-label={`Edit ${campaign.name}`} onClick={event => {event.stopPropagation(); onEdit(campaign);}}>
                            <HugeiconsIcon icon={PencilEdit01Icon} size={17}/>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete campaign">
                        <IconButton size="small" color="error" aria-label={`Delete ${campaign.name}`} onClick={event => {event.stopPropagation(); onDelete(campaign);}}>
                            <HugeiconsIcon icon={Delete02Icon} size={17}/>
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>

            <Stack gap={1} sx={{mt: "auto", color: "text.secondary"}}>
                <Stack direction="row" alignItems="center" gap={1}>
                    <CalendarTodayOutlinedIcon sx={{fontSize: 16, flexShrink: 0}}/>
                    <Typography fontSize={12}>{formatDate(campaign.startDate)} – {formatDate(campaign.endDate)}</Typography>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1}>
                    <AccessTimeIcon sx={{fontSize: 16, flexShrink: 0}}/>
                    <Typography fontSize={12}>Calling hours: {campaign.startTime || "Not set"} – {campaign.endTime || "Not set"}</Typography>
                </Stack>
            </Stack>

            <Box sx={{display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, p: 1.25, borderRadius: 2, bgcolor: "grey.50"}}>
                <Stack direction="row" alignItems="center" gap={1} sx={{minWidth: 0}}>
                    <PhoneOutlinedIcon sx={{fontSize: 17, color: "text.secondary"}}/>
                    <Box sx={{minWidth: 0}}>
                        <Typography fontSize={10} color="text.secondary">FROM NUMBER</Typography>
                        <Typography fontSize={12} fontWeight={600} noWrap title={campaign.fromNumber || "Not set"}>{campaign.fromNumber || "Not set"}</Typography>
                    </Box>
                </Stack>
                <Stack direction="row" alignItems="center" gap={1} sx={{minWidth: 0}}>
                    <TuneOutlinedIcon sx={{fontSize: 17, color: "text.secondary"}}/>
                    <Box sx={{minWidth: 0}}>
                        <Typography fontSize={10} color="text.secondary">EXTENSION</Typography>
                        <Typography fontSize={12} fontWeight={600} noWrap title={extensionLabel(extension) || "Not set"}>{extensionLabel(extension) || "Not set"}</Typography>
                    </Box>
                </Stack>
            </Box>

            <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 1}}>
                {[
                    ["Numbers", campaign.totalPhoneNumbers ?? 0],
                    ["Answered", campaign.statusCounts?.ANSWERED ?? 0],
                    ["Failed", campaign.statusCounts?.FAILED ?? 0],
                ].map(([label, value]) => (
                    <Box key={label}>
                        <Typography fontSize={22} fontWeight={600} sx={{color: "#475569", lineHeight: 1.1}}>{value}</Typography>
                        <Typography fontSize={10} color="text.secondary" sx={{textTransform: "uppercase", letterSpacing: 0.5}}>{label}</Typography>
                    </Box>
                ))}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{pt: 1.5, borderTop: "1px solid", borderColor: "grey.100"}}>
                Created {formatDate(campaign.dateCreated)}
            </Typography>
        </CardContent>
    </Card>
);
